import React, { useState } from "react";
import { Mail, Linkedin, Instagram, Facebook, ArrowRight, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", role: "", message: "" });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all the required parameters.");
      return;
    }
    toast.success(`Thank you, ${formData.name}! Your career inquiry has been successfully transmitted.`);
    setFormData({ name: "", email: "", role: "", message: "" });
  };

  return (
    <div className="relative bg-obsidian text-slate-100 min-h-screen">
      <div className="noise-overlay" />

      {/* Header */}
      <section className="relative py-20 lg:py-28 px-4 sm:px-6 lg:px-12 border-b border-white/5 overflow-hidden">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-4xl mx-auto text-left space-y-6 relative z-10">
          <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">CONTACT SUHASINI</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
            Have a question or want to explore working together?
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-normal">
            Reach out regarding bespoke consulting structures, mastermind alignment, or enterprise corporate workshops.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-12 bg-navy relative border-b border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Info Column */}
          <div className="lg:col-span-5 space-y-6 text-left">
            <h2 className="font-display text-2xl font-bold text-white">Direct Communication Details</h2>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
              We respond to validated business inquiries within 24 business hours. No spam, absolute corporate discretion guaranteed.
            </p>
            <div className="space-y-4 pt-4 border-t border-white/5 text-sm sm:text-base">
              <div className="flex items-center gap-3">
                <Linkedin className="w-5 h-5 text-gold" />
                <a href="https://www.linkedin.com/in/suhasini-basa-pmp" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white">
                  LinkedIn Profile
                </a>
              </div>
            </div>
          </div>

          {/* Form Column */}
          <div className="lg:col-span-7 bg-obsidian border border-white/10 p-6 sm:p-10 relative">
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-xs uppercase tracking-widest text-slate-400 font-mono font-bold mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Suhasini Basa"
                    data-testid="contact-name-input"
                    className="w-full bg-navy border border-white/5 text-white px-4 py-3 text-sm focus:outline-none focus:border-gold placeholder:text-slate-700 rounded-none"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs uppercase tracking-widest text-slate-400 font-mono font-bold mb-2">
                    Your Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@company.com"
                    data-testid="contact-email-input"
                    className="w-full bg-navy border border-white/5 text-white px-4 py-3 text-sm focus:outline-none focus:border-gold placeholder:text-slate-700 rounded-none"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="role" className="block text-xs uppercase tracking-widest text-slate-400 font-mono font-bold mb-2">
                  Your Current Role
                </label>
                <input
                  type="text"
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  placeholder="Senior Business Analyst / Project Manager"
                  data-testid="contact-role-input"
                  className="w-full bg-navy border border-white/5 text-white px-4 py-3 text-sm focus:outline-none focus:border-gold placeholder:text-slate-700 rounded-none"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-xs uppercase tracking-widest text-slate-400 font-mono font-bold mb-2">
                  Your Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="4"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell me about your career goals and what programs you are interested in..."
                  data-testid="contact-message-input"
                  className="w-full bg-navy border border-white/5 text-white px-4 py-3 text-sm focus:outline-none focus:border-gold placeholder:text-slate-700 rounded-none resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit-button"
                className="bg-gold hover:bg-gold-hover text-obsidian px-8 py-4 font-display font-bold text-sm uppercase tracking-widest transition-colors duration-200 inline-flex items-center gap-2"
              >
                Send Message
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>

        </div>
      </section>

    </div>
  );
}
